import React from 'react'

import { LabelVisibility } from '../../../../types'
import AutoSizeTextarea from '../AutoSizeTextarea'
import TextFieldBase from '../TextFieldBase'
import { TextFieldBaseProps, useFocusState } from '../TextFieldBase/TextFieldBase'

export type TextFieldProps = {
  /** Render as a textarea that auto expands based on content */
  autoSize?: boolean
  /** Compliant aria-label */
  ariaLabel?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Minimum number to use for a number field */
  minNumber?: number
  /** If multiline, it will render as a textarea */
  multiline?: boolean
  /** name of field. Returned with onChange callback */
  name?: string
  /**
   * Event callback when control has blurred.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onBlur?: (event: any) => void
  /**
   * Event callback text has changed.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onChange: (event: any) => void
  /**
   * Event callback when control has focused.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onFocus?: (event: any) => void
  /**
   * Event callback when key has been pressed.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onKeyDown?: (event: any) => void
  /**
   * Event callback when key has been pressed.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onKeyPress?: (event: any) => void
  /**
   * Event callback when key has been released.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onKeyUp?: (event: any) => void
  /** Placeholder text */
  placeholder?: string
  /** Used for focus/cursor handling (useFocus hook) */
  inputRef?: any
  /** The type of the input field.  Not used if the input is textarea */
  inputType?: string
  /** Number of rows for textarea */
  rows?: number
  /** Value of the control */
  value?: string | number
} & Omit<TextFieldBaseProps, 'inputComponent' | 'focused'>

const defaultProps: Omit<TextFieldProps, 'id' | 'label' | 'onChange'> = {
  autoSize: false,
  ariaLabel: undefined,
  disabled: false,
  minNumber: 0,
  multiline: undefined,
  name: undefined,
  onBlur: undefined,
  onFocus: undefined,
  onKeyDown: undefined,
  onKeyPress: undefined,
  onKeyUp: undefined,
  placeholder: undefined,
  inputRef: undefined,
  inputType: undefined,
  rows: undefined,
  value: ''
}

export const TextField = ({
  id,
  autoSize,
  ariaLabel,
  disabled,
  inputRef,
  inputType,
  labelVisibility,
  maxLength,
  minNumber,
  multiline,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onKeyPress,
  onKeyUp,
  placeholder,
  rows,
  value,
  ...props
}: TextFieldProps) => {
  const [focused, onFocusHook, onBlurHook] = useFocusState(onFocus, onBlur)
  // Not a best practice to us 'any' but its rough getting TS to
  // understand all the possible component types here
  let InputComponent: any = 'input'

  if (multiline) {
    InputComponent = 'textarea'
  } else if (autoSize) {
    InputComponent = AutoSizeTextarea
  }

  const inputTypeProps = {
    type: inputType
  }
  const inputTypeProp = !multiline && inputType ? inputTypeProps : null
  const extraProps = autoSize || multiline ? { rows } : inputTypeProp

  const labelHiddenOnFilled =
    labelVisibility === LabelVisibility.VISIBLE_ON_FOCUS && value && !focused
  const labelHidden = labelVisibility === LabelVisibility.HIDDEN
  const srOnlyLabel = labelHidden || labelHiddenOnFilled || (!focused && placeholder)
  const autoSizeProps = autoSize ? { labelHidden: srOnlyLabel } : null
  const inputComponent = (
    <InputComponent
      id={id}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      autoComplete="off"
      disabled={disabled}
      maxLength={maxLength}
      min={minNumber}
      name={name}
      onBlur={onBlurHook}
      onChange={onChange}
      onFocus={onFocusHook}
      onKeyDown={onKeyDown}
      onKeyPress={onKeyPress}
      onKeyUp={onKeyUp}
      placeholder={placeholder}
      ref={inputRef}
      value={value}
      {...extraProps}
      {...autoSizeProps}
    />
  )

  return (
    <TextFieldBase
      {...props}
      id={id}
      focused={focused}
      inputComponent={inputComponent}
      labelVisibility={labelVisibility}
      maxLength={maxLength}
      placeholder={placeholder}
      value={value}
    />
  )
}

TextField.defaultProps = defaultProps

export default TextField
